import { getOutlineControlProps } from 'utils/buttonUtils';
import type { ReactElement } from 'react';
import {
  Paper,
  Button,
  Group,
  Stack,
  Text,
  Textarea,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { RuleSeverity } from '@billing/validation/rules';
import { RULE_MESSAGE_MAX_LENGTH } from './constants';
import { RuleAlertPreview } from './RuleAlertPreview';
import type { RuleBuilder } from './useRuleBuilder';

export const RuleMessageFields = ({
  builder,
}: {
  builder: RuleBuilder;
}): ReactElement => {
  const colorScheme = useComputedColorScheme();
  const { severity, ruleMessage, setRuleMessage, translate } = builder;
  const block = severity === RuleSeverity.Block;
  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2} size="h4">
          {block
            ? translate('pages:rules.builder.blockOutcomeTitle')
            : translate('pages:rules.builder.warnOutcomeTitle')}
        </Title>
        <Text size="sm" c="dimmed">
          {translate('pages:rules.builder.messageDescription')}
        </Text>
      </Stack>
      <Stack gap="xs">
        <Textarea
          label={translate('pages:rules.builder.message')}
          placeholder={translate('pages:rules.builder.messagePlaceholder')}
          value={ruleMessage}
          onChange={(event) => setRuleMessage(event.currentTarget.value)}
          minRows={4}
          maxLength={RULE_MESSAGE_MAX_LENGTH}
          data-testid="rule-message"
        />
        <Text size="xs" c="dimmed" ta="right" aria-live="polite">
          {ruleMessage.length}/{RULE_MESSAGE_MAX_LENGTH}
        </Text>
      </Stack>
      <Stack gap="sm">
        <Text size="xs" c="dimmed" fw={700} tt="uppercase">
          {translate('pages:rules.builder.preview')}
        </Text>
        <Paper
          withBorder
          radius="md"
          p="md"
          bg="transparent"
          style={{ borderStyle: 'dashed' }}
        >
          <Stack gap="sm">
            <RuleAlertPreview
              severity={severity}
              message={ruleMessage}
              translate={translate}
            />
            <Group justify="flex-end">
              {!block && (
                <Button
                  component="span"
                  {...getOutlineControlProps(colorScheme, false)}
                  color={colorScheme === 'dark' ? 'white' : 'dark'}
                >
                  {translate('pages:rules.builder.saveAnyway')}
                </Button>
              )}
              <Button
                component="span"
                {...getOutlineControlProps(colorScheme, false)}
                color={colorScheme === 'dark' ? 'white' : 'dark'}
              >
                {translate('pages:rules.builder.fixClaim')}
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};
